/**
 * Écran « Hypothèses » (`#hypotheses`, ADR 0011, red team v0.2 B3) : tous les paramètres du moteur, groupés, avec la valeur
 * utilisée, la fourchette, la source (lien) ou le badge « non sourcé » / « paramètre de jeu (non issu de la littérature) »,
 * et la sensibilité si elle est exportée. Quelques hypothèses clés sont modifiables dans leur fourchette, puis la partie
 * est rejouée (même programme, mêmes réponses).
 */
import type { Param } from "../../../engine/src/types.js";
import { fcBadgeHtml } from "../factcheck.js";
import { escapeHtml as e, int, num } from "../format.js";
import { ADJUSTABLE_GROUPS, adjustableRange, allParamInfo, paramInfo, PARAM_GROUPS, OTHER_GROUP, type ParamInfo } from "../params-info.js";
import type { Store } from "../state.js";
import { realisticToggle } from "../components/scorecard.js";

const UNIT: Partial<Record<Param["unit"], string>> = { bn_eur: "Md€", pct_gdp: "% du PIB", pct: "%", pt: "pt", years: "ans", mt_co2e: "Mt CO₂e", persons: "", index: "", ratio: "", hours: "h", months: "mois" };

/** Valeur lisible, avec assez de décimales pour les petits coefficients. */
export function paramValue(v: number | null | undefined, unit: Param["unit"]): string {
  if (v === null || v === undefined) return "—";
  const a = Math.abs(v);
  const dec = a === 0 || a >= 100 ? 0 : a >= 10 ? 1 : a >= 1 ? 2 : a >= 0.1 ? 2 : 3;
  const u = UNIT[unit] ?? "";
  return `${num(v, Number.isInteger(v) ? 0 : dec)}${u ? ` ${u}` : ""}`;
}

function statusBadge(i: ParamInfo): string {
  if (i.status === "sourced") return `<span class="badge ok">sourcé</span>`;
  if (i.status === "game") return `<span class="badge warn" title="paramètre de jeu non sourcé, voir écran Hypothèses">paramètre de jeu (non issu de la littérature)</span>`;
  return `<span class="badge">non sourcé</span>`;
}

function sourcesHtml(i: ParamInfo): string {
  if (!i.sources.length) return "";
  return `<div class="xs">${i.sources.map((s) => `<a href="${e(s.url)}" target="_blank" rel="noopener noreferrer">${e(s.publisher)} — ${e(s.title)}</a>${s.locator ? ` (${e(s.locator)})` : ""}`).join(" · ")}</div>`;
}

function row(i: ParamInfo): string {
  const modified = i.value !== i.default;
  const range = i.low !== undefined && i.high !== undefined ? `fourchette ${paramValue(i.low, i.unit)} – ${paramValue(i.high, i.unit)}` : "sans fourchette";
  return `<li class="prm" data-param-id="${e(i.id)}">
    <div class="ph"><b>${e(i.label)}</b> ${statusBadge(i)}${i.status === "sourced" ? fcBadgeHtml(`params/${i.id}`) : ""}${i.influential ? ` <span class="badge mut">influent</span>` : ""}</div>
    <div class="sm">valeur utilisée <b class="${modified ? "warn" : ""}">${paramValue(i.value, i.unit)}</b>${modified ? ` <span class="xs">(défaut ${paramValue(i.default, i.unit)})</span>` : ""} · <span class="mut">${range}</span>${i.sensitivity !== undefined ? ` · <span class="mut">sensibilité ${num(i.sensitivity, 2)}</span>` : ""} · <code class="xs mut">${e(i.id)}</code></div>
    ${sourcesHtml(i)}${i.notes.length ? `<div class="xs mut">${i.notes.map(e).join(" ")}</div>` : ""}</li>`;
}

function sliders(store: Store): string {
  const ps = store.content.paramSources;
  const blocks = ADJUSTABLE_GROUPS.map((g) => {
    const items = g.ids
      .map((id) => paramInfo(id, store.content.params, store.ctx.params, ps, store.content.sensitivity))
      .filter((i): i is ParamInfo => !!i)
      .map((i) => {
        const r = adjustableRange(i);
        const v = i.value ?? i.default ?? 0;
        const span = r ? r.high - r.low : 0;
        const step = span > 0 ? Math.pow(10, Math.floor(Math.log10(span)) - 1) : 1;
        const kind = i.status === "sourced" ? "fourchette publiée" : "fourchette de convention, non sourcée";
        return `<div class="lever hypl">
          <div><div class="n"><label for="hp-${i.id}">${e(i.label)}</label> ${statusBadge(i)}</div>
          <div class="s">${r ? `${kind} : ${paramValue(r.low, i.unit)} – ${paramValue(r.high, i.unit)} · défaut ${paramValue(i.default, i.unit)}` : "pas de fourchette dans les données : non modifiable"}</div></div>
          <div class="val" id="vp-${i.id}">${paramValue(v, i.unit)}</div>
          <input id="hp-${i.id}" type="range" ${r ? `min="${r.low}" max="${r.high}" step="${step}"` : "disabled"} value="${v}" data-param="${i.id}" aria-valuetext="${paramValue(v, i.unit)}">
        </div>`;
      })
      .join("");
    return items ? `<h3>${e(g.label)}</h3>${items}` : "";
  }).join("");
  const n = Object.keys(store.state.paramOverrides).length;
  const st = store.state;
  const replay = st.voted ? `<button type="button" data-action="go-resultat">Rejouer ma partie avec ces hypothèses</button>` : `<button type="button" data-action="hyp-back">Continuer la partie</button>`;
  return `<div class="card"><h2>Changer quelques hypothèses clés</h2>
    <p class="mut sm">Les économistes ne sont pas d'accord sur ces coefficients. Déplace un curseur dans sa fourchette : ${st.voted ? "ta partie (même programme, mêmes réponses aux crises) est rejouée avec ces valeurs" : "la partie se jouera avec ces valeurs"}. Le résultat affichera « hypothèses modifiées » et le lien de partage les emportera.</p>
    ${blocks}
    <div class="row">${replay}${n ? `<button type="button" class="sec" data-action="hyp-reset">Rétablir les valeurs par défaut (${int(n)})</button>` : ""}</div></div>`;
}

export function renderHypotheses(store: Store): string {
  const infos = allParamInfo(store.content.params, store.ctx.params, store.content.paramSources, store.content.sensitivity);
  const count = (s: ParamInfo["status"]) => infos.filter((i) => i.status === s).length;
  const order = [...PARAM_GROUPS.map(([g]) => g), OTHER_GROUP];
  const groups = order
    .map((g) => {
      const xs = infos.filter((i) => i.group === g);
      if (!xs.length) return "";
      const todo = xs.filter((i) => i.status !== "sourced").length;
      return `<details class="cat hypg" data-hypg="${e(g)}"><summary>${e(g)}<span class="sum">${int(xs.length)} paramètre${xs.length > 1 ? "s" : ""}${todo ? ` · ${int(todo)} non sourcé${todo > 1 ? "s" : ""}` : ""}</span></summary><ul class="prms">${xs.map(row).join("")}</ul></details>`;
    })
    .join("");
  const sens = store.content.sensitivity ? "" : `<p class="hint">Sensibilité de chaque paramètre : pas encore exportée (docs/PARAM-SENSITIVITY.md).</p>`;
  return `<div class="card">
    <h1>Hypothèses du jeu</h1>
    <p class="mut">Le moteur repose sur ${int(infos.length)} paramètres. Chacun est listé ici avec la valeur utilisée, sa fourchette et sa source. Ceux qui n'ont pas de source publiée sont des <b>conventions du jeu</b> : ils servent à faire tourner la simulation, ils ne sont pas des faits. Ne pas les citer comme tels.</p>
    <p class="sm"><span class="badge ok">sourcé</span> ${int(count("sourced"))} · <span class="badge warn">paramètre de jeu</span> ${int(count("game"))} · <span class="badge">non sourcé</span> ${int(count("todo"))}</p>
    ${store.content.paramSources ? "" : `<p class="hint">Registre des sources des paramètres (params.sources.json) pas encore publié : seules les sources de params.json sont affichées.</p>`}
    ${realisticToggle(store, "rm-hyp")}
    <div class="row"><button type="button" class="sec" data-action="hyp-back">Revenir à la partie</button></div>
  </div>
  ${sliders(store)}
  <div class="card"><h2>Tous les paramètres</h2>${sens}${groups}</div>`;
}
